import { ProductDocument } from 'models/product';

type SizingFitTabProps = {
  product: ProductDocument;
};

type Sizing = {
  euro: string;
  usMens: string;
  usWomens?: string;
};

const sizingByBrand: Record<string, Sizing[]> = {
  sidi: [
    { euro: '36', usMens: '3-3.5', usWomens: '4.5-5' },
    { euro: '37', usMens: '4-4.5', usWomens: '5.5-6' },
    { euro: '38', usMens: '5-5.5', usWomens: '6.5-7' },
    { euro: '39', usMens: '5.5-6', usWomens: '7-7.5' },
    { euro: '40', usMens: '6.5-7', usWomens: '8-8.5' },
    { euro: '41', usMens: '7-7.5', usWomens: '8.5-9' },
    { euro: '42', usMens: '8-8.5', usWomens: '9.5-10' },
    { euro: '43', usMens: '9-9.5', usWomens: '10.5-11' },
    { euro: '44', usMens: '9.5-10', usWomens: '11-11.5' },
    { euro: '45', usMens: '10.5-11', usWomens: '12-12.5' },
    { euro: '46', usMens: '11-11.5', usWomens: '12.5-13' },
    { euro: '47', usMens: '12-12.5' },
    { euro: '48', usMens: '13-13.5' },
    { euro: '49', usMens: '14' },
    { euro: '50', usMens: '15' },
  ],
  alpinestars: [
    { euro: '36', usMens: '3.5' },
    { euro: '37', usMens: '4' },
    { euro: '38', usMens: '5' },
    { euro: '39', usMens: '6' },
    { euro: '40', usMens: '6.5' },
    { euro: '41', usMens: '7.5' },
    { euro: '42', usMens: '8' },
    { euro: '43', usMens: '9' },
    { euro: '44', usMens: '9.5' },
    { euro: '45', usMens: '10.5' },
    { euro: '46', usMens: '11.5' },
    { euro: '47', usMens: '12' },
    { euro: '48', usMens: '12.5' },
    { euro: '49', usMens: '13.5' },
    { euro: '50', usMens: '14' },
  ],
  tourmaster: [
    { euro: '40', usMens: '7' },
    { euro: '41', usMens: '8' },
    { euro: '42', usMens: '8.5' },
    { euro: '43', usMens: '9' },
    { euro: '44', usMens: '10' },
    { euro: '45', usMens: '11' },
    { euro: '46', usMens: '12' },
    { euro: '47', usMens: '12.5' },
    { euro: '48', usMens: '13' },
    { euro: '49', usMens: '14' },
  ],
  fox: [
    { euro: '36', usMens: '4', usWomens: '6' },
    { euro: '37', usMens: '5', usWomens: '7' },
    { euro: '38', usMens: '6', usWomens: '8' },
    { euro: '39', usMens: '6.5', usWomens: '8.5' },
    { euro: '40', usMens: '7', usWomens: '9' },
    { euro: '41', usMens: '8', usWomens: '10' },
    { euro: '42', usMens: '9', usWomens: '11' },
    { euro: '43', usMens: '10', usWomens: '12' },
    { euro: '44', usMens: '10.5', usWomens: '12.5' },
    { euro: '45', usMens: '11' },
    { euro: '46', usMens: '12' },
    { euro: '47', usMens: '13' },
    { euro: '48', usMens: '14' },
    { euro: '49', usMens: '15' },
  ],
};

export default function SizingFitTab({ product }: SizingFitTabProps) {
  const brandKey = product.brand?.toLowerCase();
  const sizeKey = Object.keys(sizingByBrand).find((key) =>
    brandKey?.includes(key)
  );
  const sizingData = sizeKey
    ? sizingByBrand[sizeKey as keyof typeof sizingByBrand]
    : null;

  // Check if product is boots
  const isBoots = ['boots'].includes(product.category1?.toLowerCase() ?? '');

  // Check if product has European sizes (30-50 range)
  const hasEuropeanSizes = (() => {
    if (product.variations?.length) {
      return product.variations.some((variation) => {
        const sizeStr = variation.size?.toString();
        const sizeNum = parseFloat(sizeStr || '');
        return sizeStr && !isNaN(sizeNum) && sizeNum >= 30 && sizeNum <= 50;
      });
    } else if (product.size?.number) {
      const sizeNum = product.size.number;
      return sizeNum >= 30 && sizeNum <= 50;
    }
    return false;
  })();

  // Only show sizing chart for boots with European sizes
  if (!isBoots || !hasEuropeanSizes || !sizingData) return null;

  return (
    <div className="rounded-b-lg border border-zinc-300 p-8">
      <div className="mt-8 hidden sm:flex">
        <div className="relative max-h-[40rem] w-full overflow-y-scroll border-[1px] border-zinc-200">
          <table className="w-full">
            <thead>
              <tr>
                <th className="sticky top-0 border-r border-[#D9D9D9] bg-[#222] p-6 text-left text-[1.8rem] font-semibold text-white">
                  EURO SIZE
                </th>
                <th className="sticky top-0 border-r border-[#D9D9D9] bg-[#222] p-6 text-left text-[1.8rem] font-semibold text-white">
                  US MENS SIZE
                </th>
                <th className="sticky top-0 bg-[#222] p-6 text-left text-[1.8rem] font-semibold text-white">
                  US WOMENS SIZE
                </th>
              </tr>
            </thead>
            <tbody>
              {sizingData.map((size, index) => (
                <tr
                  key={size.euro}
                  className={`h-[5rem] border-y border-[#D9D9D9] ${
                    index % 2 === 0 ? 'bg-transparent' : 'bg-[#F6F6F6]'
                  }`}
                >
                  <td className="break-words border-r border-[#D9D9D9] px-2 pl-4 text-[1.8rem]">
                    {size.euro}
                  </td>
                  <td className="break-words border-r border-[#D9D9D9] px-2 pl-8 text-[1.8rem]">
                    {size.usMens}
                  </td>

                  <td className="break-words border-r border-[#D9D9D9] px-2 pl-8 text-[1.8rem]">
                    {size.usWomens ?? 'n/a'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 flex w-full sm:hidden">
        <div className="flex w-full flex-col">
          {sizingData.map((size, index) => (
            <div
              className={`${
                index % 2 == 0 ? 'bg-white' : 'bg-[#F6F6F6]'
              } flex h-[18rem] w-full flex-col gap-y-8 border-[1.5px] border-[#D9D9D9] p-8`}
              key={size.euro}
            >
              <div className="flex flex-col gap-y-1">
                <span className="text-[1.8rem] font-medium text-brand-primary">
                  EURO SIZE
                </span>
                <span className="text-[1.8rem] font-semibold text-black">
                  {size.euro}
                </span>
              </div>

              <div className="flex flex-col gap-y-1">
                <span className="text-[1.8rem] font-medium text-brand-primary">
                  US MENS SIZE
                </span>
                <span className="text-[1.8rem] font-semibold text-black">
                  {size.usMens}
                </span>
              </div>

              <div className="flex flex-col gap-y-1">
                <span className="text-[1.8rem] font-medium text-brand-primary">
                  US WOMENS SIZE
                </span>
                <span className="text-[1.8rem] font-semibold text-black">
                  {size.usWomens}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
